import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  List,
  Pagination,
  Switch,
} from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import {
  Breadcrumb,
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { IconLibrary, Layout, PageSection } from '@product-framework/index';
import { MY_NOTIFICATIONS_API_PATH } from '../../apiPaths';
import NotificationListItems from '../../components/NotificationListItems';
import useNotificationInbox from '../../store/useNotificationInbox';

const PAGE_SIZE = 20;

function NotificationInboxPage() {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const provider = useDataProvider(
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default',
  );
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(true);
  const [loading, setLoading] = useState(true);
  const requestIdRef = useRef(0);
  const requestControllerRef = useRef(null);

  const showError = useCallback(
    errors =>
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={errors} />,
      }),
    [snackbar],
  );

  const loadNotifications = useCallback(
    async (signal = requestControllerRef.current?.signal) => {
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      setLoading(true);
      try {
        const result = await provider.get(
          MY_NOTIFICATIONS_API_PATH,
          {
            page,
            limit: PAGE_SIZE,
            ...(unreadOnly && { unreadOnly: true }),
          },
          {
            suppressGlobalErrorRedirect: true,
            allowTokenRenewal: true,
            signal,
          },
        );
        if (requestId !== requestIdRef.current) {
          return true;
        }
        if (!result?.ok) {
          showError(result?.errors);
          return false;
        }
        const items = result?.data?.data ?? [];
        setNotifications(items);
        const nextTotal = result?.data?.total ?? 0;
        setTotal(nextTotal);
        return true;
      } catch (error) {
        if (requestId === requestIdRef.current) {
          showError(error?.result?.errors || error?.errors);
        }
        return false;
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [page, provider, showError, unreadOnly],
  );

  useEffect(() => {
    const controller = new AbortController();
    requestControllerRef.current = controller;
    loadNotifications(controller.signal);
    return () => {
      requestIdRef.current += 1;
      controller.abort();
    };
  }, [loadNotifications]);

  const reloadAfterMarkAll = useCallback(async () => {
    if (page === 1) {
      await loadNotifications();
      return;
    }
    setPage(1);
  }, [loadNotifications, page]);

  const { markAllRead, markRead, markingAllRead, unreadCount } =
    useNotificationInbox({
      setNotifications,
      setTotal,
      removeOnRead: unreadOnly,
      reloadOnReadFailure: loadNotifications,
      refreshNotifications: loadNotifications,
      onMarkAllRead: reloadAfterMarkAll,
      onMarkReadError: showError,
      onMarkAllError: showError,
    });

  const openNotification = async notification => {
    await markRead(notification);
    if (notification?.linkPath) {
      navigate(notification.linkPath);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  useEffect(() => {
    if (!markingAllRead && page > pageCount) {
      setPage(pageCount);
    }
  }, [markingAllRead, page, pageCount]);

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => navigate('/dashboard'),
    },
    {
      name: formatMessage({
        id: 'NOTIFICATIONS',
        defaultMessage: 'Notifications',
      }),
      icon: IconLibrary.Alerts,
      onClick: undefined,
    },
  ];

  return (
    <Layout>
      <Breadcrumb items={breadCrumb} />
      <PageSection
        label={formatMessage({
          id: 'NOTIFICATION_CENTER',
          defaultMessage: 'Notification Center',
        })}>
        <Grid2
          xs={12}
          container
          alignItems="center"
          justifyContent="space-between">
          <Grid2 xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={unreadOnly}
                  onChange={event => {
                    setPage(1);
                    setUnreadOnly(event.target.checked);
                  }}
                />
              }
              label={formatMessage({
                id: 'UNREAD_ONLY',
                defaultMessage: 'Unread only',
              })}
            />
          </Grid2>
          <Grid2 xs={12} md={6} sx={{ textAlign: { md: 'right' } }}>
            <Button
              size="small"
              sx={{ mr: 1 }}
              startIcon={<IconLibrary.NotificationPreferences />}
              onClick={() => navigate('/notifications/preferences')}>
              {formatMessage({
                id: 'MANAGE_PREFERENCES',
                defaultMessage: 'Manage Preferences',
              })}
            </Button>
            <Button
              size="small"
              startIcon={<IconLibrary.Yes />}
              disabled={markingAllRead || unreadCount === 0}
              onClick={markAllRead}>
              {formatMessage({
                id: 'MARK_ALL_AS_READ',
                defaultMessage: 'Mark all as read',
              })}
            </Button>
          </Grid2>
        </Grid2>
        <List>
          <NotificationListItems
            notifications={notifications}
            loading={loading}
            onSelect={openNotification}
            emptyMessage={
              unreadOnly
                ? formatMessage({
                    id: 'YOURE_ALL_CAUGHT_UP',
                    defaultMessage: "You're all caught up",
                  })
                : undefined
            }
          />
        </List>
        {pageCount > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(event, value) => setPage(value)}
            />
          </Box>
        )}
      </PageSection>
    </Layout>
  );
}

export default observer(NotificationInboxPage);
